import React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle
} from "../../../ui/dialog";
import { ShortcutDisplay } from "./ShortcutDisplay";
import { GLOBAL_SHORTCUTS, SHAPE_SELECTION_SHORTCUTS } from "./shortcuts";

const WALL_PATH_VERTEX_SHORTCUTS: Record<string, string | string[]> = {
  insertVertex: "alt+click",
  removeVertex: "double-click",
  splitPathAtVertex: "alt+double-click"
};

const WALL_DRAWING_SHORTCUTS: Record<string, string | string[]> = {
  orthoAngles: "shift",
  disableWallSnap: "alt"
};

const SHORTCUT_LABELS: Record<string, string> = {
  panTool: "Pan Tool",
  drawTool: "Rectangle Tool",
  circleTool: "Circle Tool",
  drawSelectTool: "Draw Selection Tool",
  iconTool: "Icon Tool",
  selectionTool: "Selection Tool",
  copy: "Copy",
  paste: "Paste",
  undo: "Undo",
  redo: "Redo",
  selectAll: "Select All",
  groupSelected: "Group",
  unGroupSelected: "Ungroup",
  deselectAll: "Deselect All",
  wallBridgeHorizontal: "Wall Bridge (Horizontal)",
  wallBridgeVertical: "Wall Bridge (Vertical)",
  wallPathTool: "Wall Path Tool",
  addDoor: "Add Door",
  addWindow: "Add Window",
  removeDoor: "Remove Door (hover)",
  removeWindow: "Remove Window (hover)",
  wallExtend: "Extend Wall",
  duplicate: "Duplicate",
  showHide: "Show / Hide",
  lockUnlock: "Lock / Unlock",
  moveToFront: "Move Forward",
  moveToBack: "Move Backward",
  delete: "Delete",
  cut: "Cut",
  closeWallRect: "Close Wall Rectangle",
  insertVertex: "Insert Vertex (on vertex)",
  removeVertex: "Remove Vertex (on vertex)",
  splitPathAtVertex: "Split Path (on vertex)",
  orthoAngles: "Snap to 45° Angles (hold)",
  disableWallSnap: "Disable Wall Snapping (hold)"
};

interface ShortcutRowProps {
  label: string;
  keys: string | string[];
}

const ShortcutRow: React.FC<ShortcutRowProps> = ({ label, keys }) => (
  <div className="flex items-center justify-between px-1 py-1.5">
    <span className="text-neutral-700 text-sm">{label}</span>
    <ShortcutDisplay keys={keys} />
  </div>
);

interface ShortcutSectionProps {
  title: string;
  shortcuts: Record<string, string | string[]>;
}

const ShortcutSection: React.FC<ShortcutSectionProps> = ({
  title,
  shortcuts
}) => (
  <div>
    <h3 className="mb-2 font-semibold text-[11px] text-neutral-400 uppercase tracking-wider">
      {title}
    </h3>
    <div className="divide-y divide-neutral-100">
      {Object.entries(shortcuts).map(([key, keys]) => (
        <ShortcutRow
          key={key}
          label={SHORTCUT_LABELS[key] ?? key}
          keys={keys}
        />
      ))}
    </div>
  </div>
);

interface KeyboardShortcutsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const KeyboardShortcutsDialog: React.FC<
  KeyboardShortcutsDialogProps
> = ({ open, onOpenChange }) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[80vh] max-w-md overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Keyboard Shortcuts</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-6">
          <ShortcutSection title="General" shortcuts={GLOBAL_SHORTCUTS} />
          <ShortcutSection
            title="Selection"
            shortcuts={SHAPE_SELECTION_SHORTCUTS}
          />
          <ShortcutSection
            title="Wall Drawing"
            shortcuts={WALL_DRAWING_SHORTCUTS}
          />
          <ShortcutSection
            title="Wall Path Vertex"
            shortcuts={WALL_PATH_VERTEX_SHORTCUTS}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
};
